package nl.volleyplanner.web.controller.userprofile

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseAuthException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
import dev.gitlive.firebase.auth.auth
import kotlinx.browser.document
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.AcceptUserInviteRequest
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.bootstrapAlert
import nl.volleyplanner.web.base.component.validatingForm
import nl.volleyplanner.web.base.routing.PageMountContext
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement
import org.w3c.xhr.FormData

enum class InviteProcessStateStatus {
    Processing,
    Accepted,
    Exception
}

data class InviteProcessState(
    val status: InviteProcessStateStatus,
    val message: String?,
    val throwable: Throwable? = null
)

class UserInviteComponent(val organisationId: String, val email: String, val token: String) : HTMLComponent() {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.controller.userprofile.UserInviteComponent")
    }

    val rootClientFlow = AuthContextService.authUserState
        .map { authUser ->
            val tokenLoader = VolleyPlannerWebAuthTokenLoader.takeIf { authUser?.authLevel == AuthLevel.AdminUser }
            VolleyplannerApiClientFactory.createVolleyplannerApiRootClient(tokenLoader)
        }.stateIn(componentScope, SharingStarted.Eagerly, VolleyplannerApiClientFactory.createVolleyplannerApiRootClient(null))

    @OptIn(ExperimentalCoroutinesApi::class)
    private val userInviteFlow = rootClientFlow
        .flatMapLatest { rootClient ->
            ApiDataStateFlow(
                "UserInviteFlow",
                AuthLevel.AnonymousPublic,
                contextFlow = MutableStateFlow(Unit)
            ) {
                rootClient.verifyOrganisationUserInvite(
                    organisationId = organisationId,
                    email = email,
                    token = token
                )
            }.subscribeApiResult()
        }.stateIn(componentScope, SharingStarted.Eagerly, null)

    private val formSubmissionProgressFlow = MutableStateFlow(false)
    private val inviteProcessStateFlow: MutableStateFlow<InviteProcessState?> = MutableStateFlow(null)

    private fun updateFormStatus(alertColor: BootstrapColor, message: String) {
        val statusElement = document.getElementById("vp-user-invite-details-form-status")!! as HTMLElement

        statusElement.removeClass("d-none")
        statusElement.clear()

        statusElement.append(document.create.div (classes = "alert alert-${alertColor.cssName} d-flex align-items-center") {
            role = "alert"
            +message
        })
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val userInvite = userInviteFlow.value?.data
        val userInviteError = userInviteFlow.value?.error
        val authUser = AuthContextService.authUser
        val userProfile = WebUserAndOrganisationService.userProfileState.value
        val inviteProcessState = inviteProcessStateFlow.value

        return consumer.div {
            if (userInviteError != null) {
                h2 { +"Aanmelden bij vereniging" }
                bootstrapAlert(BootstrapColor.Danger) {
                    +"Kan de uitnodiging niet laden. Mogelijk is de link verlopen of ongeldig."
                }
            } else if (
                userInvite == null ||
                authUser == null ||
                // If we have an actual user, we should also wait for the DB User info to be available
                (authUser.authLevel == AuthLevel.AdminUser && userProfile == null)) {
                spinner()
            } else if (inviteProcessState != null) {
                h2 { +"Aanmelden bij vereniging" }
                when (inviteProcessState.status) {
                    InviteProcessStateStatus.Processing -> {
                        p(classes = "lead") { +"Je bent door ${userInvite.organisationName} uitgenodigd om je aan te melden. Voer je gegevens in om door te gaan." }

                        p {
                            spinner()
                            +"Bezig met aanmelden..."
                        }
                    }
                    InviteProcessStateStatus.Accepted -> {
                        if (userProfile != null) {
                            p(classes = "lead") { +"Je bent nu aangemeld bij ${userInvite.organisationName}" }

                            button(classes = "btn btn-primary") {
                                onClickFunction = { event ->
                                    event.preventDefault()

                                    componentScope.launch {
                                        val organisation = WebUserAndOrganisationService.availableOrganisationsState
                                            .filter { it?.any { it.identifier == organisationId } == true }
                                            .stateIn(componentScope, SharingStarted.Eagerly, null)
                                            .filterNotNull()
                                            .first()
                                            .first { it.identifier == organisationId }

                                        WebUserAndOrganisationService.changeActiveOrganisation(organisation)
                                    }
                                }
                                +"Naar vereniging"
                            }
                        } else {
                            p(classes = "lead") { +"Je bent nu aangemeld bij ${userInvite.organisationName} en kan nu inloggen." }

                            a (href = "/login") {
                                role = "button"
                                classes += "btn btn-primary"

                                +"Inloggen"
                            }
                        }
                    }
                    InviteProcessStateStatus.Exception -> {
                        bootstrapAlert(BootstrapColor.Danger) {
                            icon("bi-exclamation-triangle")
                            p {
                                strong { +"Er is iets misgegaan tijdens het aanmelden: " }
                                +(inviteProcessState.message ?: "")
                            }
                        }
                    }
                }
            } else {
                val emailForForm = authUser.email ?: userInvite.email
                val nameForForm = userProfile?.name ?: userInvite.name
                val isLoggedIn = authUser.authLevel == AuthLevel.AdminUser
                val emailLocked = authUser.email != null
                val userExists = userInvite.authUserExists

                h2 { +"Aanmelden bij vereniging" }
                p(classes = "lead") { +"Je bent door ${userInvite.organisationName} uitgenodigd om je aan te melden. Voer je gegevens in om door te gaan." }

                if (isLoggedIn && userInvite.email != authUser.email) {
                    bootstrapAlert(BootstrapColor.Danger) {
                        icon("bi-exclamation-triangle")
                        p {
                            strong { +"Let op! " }
                            +"Je bent momenteel al ingelogd als "
                            strong { +(authUser.email ?: "") }
                            +". Log eerst uit om je met "
                            strong { +userInvite.email }
                            +" aan te melden."
                        }
                        p {
                            +"Of vraag de beheerder van ${userInvite.organisationName} om je uit te nodigen met je huidige email: ${authUser.email}."
                        }
                    }
                } else {
                    if (authUser.authLevel == AuthLevel.AdminUser) {
                        bootstrapAlert(BootstrapColor.Info) {
                            +"Omdat je al een account hebt en bent ingelogd zijn je gegevens vooraf ingevuld."
                        }
                    }
                    validatingForm(
                        validSubmitFunction = { event, formElement ->
                            event.preventDefault()

                            formSubmissionProgressFlow.value = true

                            val formData = FormData(formElement)
                            val name = formData.get("vp-user-name") as String
                            val email = formData.get("vp-user-email") as String
                            val password = formData.get("vp-user-password") as? String?

                            logger.debug { "Submitting user invite accept: name=$name, email=$email" }

                            // If we are already logged in, flow is simple: just add to organisation
                            val acceptUserInviteRequest = AcceptUserInviteRequest(
                                userInvite.email,
                                userInvite.token,
                                email,
                                name,
                                password
                            )

                            // Check if a user exists for the email
                            // In that case try to login (if not already logged in)
                            if (userExists && authUser.authLevel != AuthLevel.AdminUser) {
                                val result = try {
                                    logger.debug { "Not logged in, but user should exist: logging in user $email" }
                                    Firebase.auth.signInWithEmailAndPassword(email, password!!)
                                } catch (e: FirebaseAuthInvalidCredentialsException) {
                                    updateFormStatus(BootstrapColor.Danger, "Gebruiker $email bestaat al, verkeerd wachtwoord opgegeven.")
                                    formSubmissionProgressFlow.value = false
                                    return@validatingForm
                                } catch (e: FirebaseAuthException) {
                                    updateFormStatus(BootstrapColor.Danger, "Inloggen voor $email mislukt: ${e.message}")
                                    formSubmissionProgressFlow.value = false
                                    return@validatingForm
                                } catch (e: Exception) {
                                    updateFormStatus(BootstrapColor.Danger, "Er is iets misgegaan met het aanmelden: ${e.message}")
                                    formSubmissionProgressFlow.value = false
                                    return@validatingForm
                                }
                                if (result.user == null) {
                                    // Auth failed
                                    updateFormStatus(BootstrapColor.Danger, "Gebruiker $email bestaat al, verkeerd wachtwoord opgegeven.")
                                    formSubmissionProgressFlow.value = false
                                    return@validatingForm
                                } else {
                                    logger.debug { "Logged in user $email, awaiting auth state update... current: ${AuthContextService.authUserState.value?.email}" }
                                    AuthContextService.authUserState
                                        .filterNotNull()
                                        .first { it.email == result.user!!.email }

                                    inviteProcessStateFlow.value = InviteProcessState(
                                        InviteProcessStateStatus.Processing, null
                                    )

//                                    logger.debug { "Awaiting user profile state update... current: ${WebUserAndOrganisationService.userProfileState.value?.email}" }
//                                    WebUserAndOrganisationService.userProfileState
//                                        .filterNotNull()
//                                        .first { it.email == result.user?.email }

                                    logger.debug { "Resetting root API client authentication" }
                                    // TODO: Do automatically if logged in user changes
                                    VolleyPlannerApplication.volleyplannerRootApiClient.resetAuth()
                                }
                            }
                            inviteProcessStateFlow.value = InviteProcessState(
                                InviteProcessStateStatus.Processing, null
                            )

                            // Need to get latest authuser as the previous step might have logged in a new user
                            val authUser = AuthContextService.authUserState.value!!
                            logger.debug { "Initiating accepting user invite. Authuser: ${authUser.email}" }
                            launchAsync {
                                logger.debug { "Accepting invite API request" }
                                rootClientFlow.value.acceptOrganisationUserInvite(
                                    organisationId, acceptUserInviteRequest
                                )
                                logger.debug { "Refresh organisations and profile: ${authUser.authLevel}" }
                                if (authUser.authLevel == AuthLevel.AdminUser) {
                                    WebUserAndOrganisationService.refreshAvailableOrganisations()
                                    WebUserAndOrganisationService.refreshUserProfile()
                                }
                            }.onComplete {
                                logger.debug { "Invite process complete" }
                                formSubmissionProgressFlow.value = false
                                inviteProcessStateFlow.value = InviteProcessState(
                                    InviteProcessStateStatus.Accepted, null
                                )
                            }.onError { throwable ->
                                logger.warn { "Invite process errors: $throwable" }
                                formSubmissionProgressFlow.value = false
                                inviteProcessStateFlow.value = InviteProcessState(
                                    InviteProcessStateStatus.Exception, throwable.message, throwable
                                )
                            }
                        }
                    ) {
                        id = "vp-user-invite-details-form"

                        div(classes = "d-none") {
                            id = "vp-user-invite-details-form-status"
                        }

                        div(classes = "p-3 mb-2") {
                            fieldSet {
                                div(classes = "row g-3 mb-4 align-items-center") {
                                    label(classes = "col-12 col-sm-3 col-form-label") {
                                        htmlFor = "vp-user-name"
                                        +"Naam"
                                    }
                                    div(classes = "col-12 col-sm-9") {
                                        input(type = InputType.text, classes = "form-control") {
                                            id = "vp-user-name"
                                            readonly = emailLocked

                                            name = "vp-user-name"
                                            value = nameForForm
                                            autoComplete = "name"

                                            required = true
                                        }
                                        div(classes = "invalid-feedback") {
                                            +"Een naam is verplicht."
                                        }
                                    }
                                }
                                div(classes = "row g-3 mb-4 align-items-center") {
                                    label(classes = "col-12 col-sm-3 col-form-label") {
                                        htmlFor = "vp-user-email"
                                        +"Email"
                                    }
                                    div(classes = "col-12 col-sm-9") {
                                        input(type = InputType.text, classes = "form-control") {
                                            id = "vp-user-email"
                                            readonly = emailLocked

                                            name = "vp-user-email"
                                            value = emailForForm
                                            if (!emailLocked) {
                                                autoComplete = "username"
                                            }

                                            required = true
                                        }
                                        div(classes = "invalid-feedback") {
                                            +"Een email adres is verplicht."
                                        }
                                    }
                                }
                                if (authUser.authLevel != AuthLevel.AdminUser) {
                                    div(classes = "row g-3 mb-4 align-items-center") {
                                        label(classes = "col-12 col-sm-3 col-form-label") {
                                            htmlFor = "vp-user-password"
                                            +"Wachtwoord"
                                        }
                                        div(classes = "col-12 col-sm-9") {
                                            input(type = InputType.password, classes = "form-control") {
                                                id = "vp-user-password"
                                                name = "vp-user-password"

                                                autoComplete = "current-password"

                                                required = true
                                                minLength = "8"
                                            }
                                            div(classes = "invalid-feedback") {
                                                +"Een wachtwoord moet minimaal 8 karakters lang zijn."
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        component(ListeningComponent("InviteFormSubmitFlow", listOf(formSubmissionProgressFlow)) {
                            val inProgress = formSubmissionProgressFlow.value
                            if (inProgress) {
                                button (classes = "btn btn-secondary") {
                                    disabled = true

                                    span (classes = "spinner-border spinner-border-sm") {  }
                                    span { +" Aanmelden bij ${userInvite.organisationName}" }

                                }
                            } else {
                                input (classes = "btn btn-primary") {
                                    type = InputType.submit
                                    value = "Aanmelden bij ${userInvite.organisationName}"

                                }
                            }
                        })
                    }
                }
            }
        }
    }

    override suspend fun getState() = combine(
        userInviteFlow,
        AuthContextService.authUserState,
        WebUserAndOrganisationService.userProfileState,
        inviteProcessStateFlow
    ) { userInvite, authUser, userProfile, inviteProcessing ->
        listOf(userInvite, authUser, userProfile, inviteProcessing)
    }.stateIn(componentScope, SharingStarted.Eagerly, null)
}

class UserInviteController : RootPageController() {
    override val name = "UserInviteController"

    override val title = "Registeren"
    override val path = "/invite/:organisationId"
    override val requiredAuthLevel: AuthLevel = AuthLevel.AnonymousPublic
    override val childPages: List<WebPageController> = emptyList()

    private val organisationIdState = MutableStateFlow<String?>(null)
    private val inviteEmailState = MutableStateFlow<String?>(null)
    private val inviteTokenState = MutableStateFlow<String?>(null)

    override fun createRuntimeInstance() = UserInviteController()

    override suspend fun mount(context: PageMountContext) {
        organisationIdState.value = context.pageRouteContext.pathParameters.getValue("organisationId")
        inviteEmailState.value = context.pageRouteContext.urlState.queryParameters["email"]
        inviteTokenState.value = context.pageRouteContext.urlState.queryParameters["token"]
        super.mount(context)
    }

    override suspend fun renderContent(): HTMLElement {
        val (organisationId, email, token) = Triple(organisationIdState.value, inviteEmailState.value, inviteTokenState.value)

        return document.create.div {
            if (organisationId == null || email == null || token == null) {
                p { +"Ongeldige URL" }
            } else {
                component(UserInviteComponent(organisationId, email, token))
            }
        }
    }
}